import { useState } from 'react';
import { NavLink } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { orgsApi } from '@/api/organizations';
import { authApi } from '@/api/auth';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/cn';
import {
  LayoutDashboard,
  FileText,
  BarChart3,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Building2,
  ChevronDown,
  Plus,
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', label: 'Painel', icon: LayoutDashboard },
  { to: '/forms', label: 'Formularios', icon: FileText },
  { to: '/analytics', label: 'Analiticos', icon: BarChart3 },
  { to: '/team', label: 'Equipe', icon: Users },
  { to: '/billing', label: 'Faturamento', icon: CreditCard },
  { to: '/settings', label: 'Configuracoes', icon: Settings },
];

export function Sidebar() {
  const { user, organizations, logout } = useAuth();
  const qc = useQueryClient();
  const currentOrg = organizations.find((o) => o.id === user?.orgId) ?? organizations[0];
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  const createOrgMutation = useMutation({
    mutationFn: (name: string) => orgsApi.create(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.auth.me });
      setShowCreateOrg(false);
      setNewOrgName('');
    },
  });

  const switchOrgMutation = useMutation({
    mutationFn: (orgId: string) => authApi.switchOrg(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.auth.me });
      qc.invalidateQueries({ queryKey: queryKeys.forms.all });
      setShowOrgMenu(false);
    },
  });

  return (
    <aside className="hidden lg:flex lg:flex-col w-60 bg-(--color-sidebar) text-(--color-sidebar-text)">
      <div className="px-5 py-5 border-b border-white/10">
        <h1 className="text-lg font-bold text-(--color-sidebar-active) font-(family-name:--font-display) tracking-tight">
          ZP Forms
        </h1>
      </div>

      {/* Org Switcher */}
      <div className="px-3 pt-4 pb-2 relative">
        <button
          onClick={() => setShowOrgMenu(!showOrgMenu)}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-(--color-sidebar-hover) hover:bg-white/10 transition-colors"
        >
          <Building2 className="h-4 w-4 shrink-0" />
          <span className="text-sm truncate flex-1 text-left text-(--color-sidebar-active)">
            {currentOrg?.name ?? 'Organizacao'}
          </span>
          <ChevronDown className={cn('h-3 w-3 transition-transform', showOrgMenu && 'rotate-180')} />
        </button>

        {showOrgMenu && (
          <div className="absolute left-3 right-3 top-full mt-1 bg-gray-800 rounded-lg shadow-lg border border-white/10 z-50 overflow-hidden">
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => {
                  if (org.id !== currentOrg?.id) switchOrgMutation.mutate(org.id);
                  else setShowOrgMenu(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors',
                  org.id === currentOrg?.id ? 'text-primary font-medium' : 'text-(--color-sidebar-text)',
                )}
              >
                {org.name}
              </button>
            ))}
            <div className="border-t border-white/10">
              {showCreateOrg ? (
                <div className="p-2">
                  <input
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    placeholder="Nome da organizacao"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newOrgName) createOrgMutation.mutate(newOrgName);
                      if (e.key === 'Escape') setShowCreateOrg(false);
                    }}
                    className="w-full px-2 py-1 text-sm bg-gray-700 rounded border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:border-primary"
                  />
                  <div className="flex gap-1 mt-1">
                    <button
                      onClick={() => { if (newOrgName) createOrgMutation.mutate(newOrgName); }}
                      disabled={!newOrgName || createOrgMutation.isPending}
                      className="flex-1 px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
                    >
                      {createOrgMutation.isPending ? 'Criando...' : 'Criar'}
                    </button>
                    <button
                      onClick={() => { setShowCreateOrg(false); setNewOrgName(''); }}
                      className="px-2 py-1 text-xs text-gray-400 hover:text-white"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateOrg(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <Plus className="h-3 w-3" /> Nova organizacao
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-2 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                isActive
                  ? 'bg-(--color-sidebar-hover) text-(--color-sidebar-active) font-medium'
                  : 'hover:bg-(--color-sidebar-hover) hover:text-(--color-sidebar-active)',
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-white/10">
        <div className="px-3 py-2 text-xs truncate">{user?.email}</div>
        <button
          onClick={() => logout()}
          className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm hover:bg-(--color-sidebar-hover) hover:text-(--color-sidebar-active) transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </button>
      </div>
    </aside>
  );
}
